import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  ListGroup,
  Badge,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  FiUser,
  FiShoppingBag,
  FiCreditCard,
  FiLogOut,
  FiPhone,
  FiUserCheck,
  FiCalendar,
  FiEdit,
  FiCheckCircle,
} from "react-icons/fi";
import { orderAPI, userAPI } from "../utils/api";

const MyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 사용자 정보 상태
  const [userData, setUserData] = useState({
    id: "",
    username: "",
    name: "",
    phone: "",
    type: "",
    status: "",
    account: "",
    created_at: "",
  });

  // 모달 및 폼 편집 상태
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    phone: "",
    account: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  const [recentOrders, setRecentOrders] = useState([]);
  const isAuthenticated = localStorage.getItem("accessToken") ? true : false;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { state: { returnUrl: "/mypage" } });
      return;
    }

    loadUserData();
    loadRecentOrders();
  }, [isAuthenticated, navigate]);

  // 사용자 정보 로드
  const loadUserData = async () => {
    try {
      const response = await userAPI.getProfile();
      if (response && response.data) {
        const u = response.data;
        setUserData({
          id: u.id || "",
          username: u.username || "",
          name: u.name || "",
          phone: u.phone || "",
          type: u.type || "",
          status: u.status || "",
          account: u.account || "",
          created_at: u.created_at || "",
        });
      }
    } catch (err) {
      console.error("사용자 정보 로드 오류:", err);
      setError("사용자 정보를 불러오는데 실패했습니다.");

      // Fallback
      const username = localStorage.getItem("username") || "사용자";
      setUserData(prev => ({
        ...prev,
        username,
        name: username,
        phone: "010-0000-0000",
        type: "Normal",
      }));
    }
  };

  // 모달 열기
  const handleOpenEditModal = () => {
    setEditFormData({
      name: userData.name,
      phone: userData.phone,
      account: userData.account,
    });
    setShowEditModal(true);
  };

  // 모달 닫기
  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  // 프로필 수정 제출
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!userData.id) {
      alert("사용자 식별 정보가 없습니다.");
      return;
    }

    setEditLoading(true);
    try {
      const response = await userAPI.updateUserById(userData.id, {
        name: editFormData.name,
        phone: editFormData.phone,
        account: editFormData.account,
      });

      if (response && response.data) {
        const u = response.data;
        setUserData(prev => ({
          ...prev,
          name: u.name || prev.name,
          phone: u.phone || prev.phone,
          account: u.account || prev.account,
        }));

        // 헤더 등 사이트 전역 닉네임 유지를 위해 로컬스토리지 동기화
        localStorage.setItem("username", u.name || u.username);
        alert("회원 정보가 성공적으로 수정되었습니다.");
        setShowEditModal(false);
      }
    } catch (err) {
      console.error("프로필 수정 오류:", err);
      alert(err.response?.data?.message || "프로필 정보 수정 중 오류가 발생했습니다.");
    } finally {
      setEditLoading(false);
    }
  };

  // 최근 주문 내역 로드
  const loadRecentOrders = () => {
    setLoading(true);
    try {
      orderAPI
        .getAll()
        .then((response) => {
          const orders = response.data.results || [];
          setRecentOrders(orders.slice(0, 3));
          localStorage.setItem("orders", JSON.stringify(orders));
          setLoading(false);
        })
        .catch((err) => {
          console.error("API 주문 내역 로드 오류:", err);
          const savedOrders = JSON.parse(
            localStorage.getItem("orders") || "[]"
          );
          setRecentOrders(savedOrders.slice(0, 3));
          setLoading(false);
        });
    } catch (err) {
      console.error("주문 내역 로드 오류:", err);
      setError("최근 주문 내역을 불러오는데 실패했습니다.");
      const savedOrders = JSON.parse(localStorage.getItem("orders") || "[]");
      setRecentOrders(savedOrders.slice(0, 3));
      setLoading(false);
    }
  };

  // 로그아웃 처리
  const handleLogout = () => {
    if (window.confirm("로그아웃 하시겠습니까?")) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("username");
      localStorage.removeItem("isKakao");
      navigate("/login");
    }
  };

  // 회원 종류 매핑
  const getUserTypeLabel = (type) => {
    switch (type) {
      case "Normal":
        return "일반 회원";
      case "Manager":
        return "사찰 / 스님 회원";
      case "Admin":
        return "관리자";
      default:
        return "일반 회원";
    }
  };

  // 승인 상태 라벨 매핑
  const getStatusLabel = (status) => {
    switch (status) {
      case "APPROVED":
        return "승인 완료";
      case "PENDING":
        return "승인 대기";
      case "REJECTED":
        return "승인 반려";
      default:
        return "일반";
    }
  };

  // 주문 상태 텍스트
  const getStatusText = (status) => {
    switch (status) {
      case "DONE":
        return "기도 완료";
      case "PROCESS":
        return "사찰에서 기도 중";
      case "Cancel":
        return "기도 취소";
      case "REJECT":
        return "사찰에서 기도 거절 됨";
      case "PREPARE":
        return "사찰에서 기도 준비 중";
      default:
        return "완료";
    }
  };

  // 주문 상태 배지 색상
  const getStatusColor = (status) => {
    switch (status) {
      case "DONE":
        return "secondary";
      case "PROCESS":
        return "primary";
      case "Cancel":
      case "REJECT":
        return "danger";
      case "PREPARE":
        return "warning";
      default:
        return "success";
    }
  };

  // 금액 포맷
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ko-KR").format(amount);
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("ko-KR", options);
  };

  return (
    <Container className="py-5" style={{ minHeight: "80vh" }}>
      <style>{`
        .mypage-title {
          font-weight: 800;
          color: #1a202c;
          margin-bottom: 2rem;
          letter-spacing: -0.5px;
        }

        .profile-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 149, 144, 0.03);
          border: 1px solid rgba(0, 149, 144, 0.06);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .profile-avatar-container {
          display: flex;
          justify-content: center;
          align-items: center;
          background: linear-gradient(135deg, rgba(0, 149, 144, 0.08) 0%, rgba(0, 120, 115, 0.03) 100%);
          width: 88px;
          height: 88px;
          border-radius: 50%;
          margin: 0 auto 1.25rem auto;
          border: 4px solid #ffffff;
          box-shadow: 0 4px 15px rgba(0, 149, 144, 0.08);
        }

        .profile-name {
          font-weight: 700;
          color: #2d3748;
          font-size: 1.35rem;
          margin-bottom: 0.5rem;
        }

        .profile-detail-item {
          display: flex;
          align-items: center;
          padding: 0.85rem 0;
          border-bottom: 1px solid #edf2f7;
          font-size: 0.92rem;
        }

        .profile-detail-item:last-child {
          border-bottom: none;
        }

        .profile-detail-label {
          color: #718096;
          width: 100px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .profile-detail-value {
          color: #2d3748;
          font-weight: 600;
          flex-grow: 1;
        }

        .menu-list-group .list-group-item {
          border: none;
          border-bottom: 1px solid #f7fafc;
          padding: 1rem 1.25rem;
          font-size: 0.95rem;
          color: #4a5568;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
        }

        .menu-list-group .list-group-item:last-child {
          border-bottom: none;
        }

        .menu-list-group .list-group-item:hover {
          background-color: #f7fafc;
          color: var(--main-color);
          padding-left: 1.5rem;
        }

        .section-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 149, 144, 0.03);
          border: 1px solid rgba(0, 149, 144, 0.06);
          overflow: hidden;
        }

        .section-card-header {
          background: #ffffff;
          border-bottom: 1px solid #f7fafc;
          padding: 1.25rem 1.5rem;
        }

        .section-card-title {
          font-weight: 700;
          color: #2d3748;
          font-size: 1.15rem;
          margin-bottom: 0;
        }

        .edit-modal-content {
          border-radius: 20px;
          border: none;
          overflow: hidden;
        }

        .edit-modal-header {
          background: linear-gradient(135deg, var(--main-color) 0%, var(--main-hover) 100%);
          color: #ffffff;
          border: none;
          padding: 1.5rem;
        }

        .edit-modal-title {
          font-weight: 700;
          font-size: 1.2rem;
        }

        .form-input-custom {
          border-radius: 10px;
          padding: 0.75rem 1rem;
          border: 1px solid #edf2f7;
          background-color: #f7fafc;
          transition: all 0.2s ease;
          font-size: 0.95rem;
        }
        
        .form-input-custom:focus {
          border-color: var(--main-color);
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(0, 149, 144, 0.15);
          outline: none;
        }

        .btn-custom-primary {
          background: linear-gradient(135deg, var(--main-color) 0%, var(--main-hover) 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          transition: all 0.25s ease;
        }

        .btn-custom-primary:hover:not(:disabled) {
          box-shadow: 0 4px 15px rgba(0, 149, 144, 0.2);
          transform: translateY(-1px);
        }

        .btn-custom-outline {
          background: #ffffff;
          border: 1.5px solid var(--main-color);
          color: var(--main-color);
          border-radius: 10px;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          transition: all 0.25s ease;
        }

        .btn-custom-outline:hover {
          background: rgba(0, 149, 144, 0.05);
          color: var(--main-hover);
          border-color: var(--main-hover);
        }
      `}</style>

      <h1 className="mypage-title">마이페이지</h1>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4" style={{ borderRadius: "12px" }}>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={4} className="mb-4">
          {/* 사용자 정보 카드 */}
          <Card className="profile-card mb-4 p-4 border-0">
            <Card.Body className="p-0">
              <div className="profile-avatar-container">
                <FiUser size={38} style={{ color: "var(--main-color)" }} />
              </div>

              <div className="text-center mb-4">
                <div className="profile-name">{userData.name || "사용자"}</div>
                <div className="d-flex justify-content-center gap-2 mb-2">
                  <Badge bg="light" style={{ backgroundColor: "rgba(218, 218, 218, 1) !important", color: "var(--main-color)", fontSize: "0.78rem", fontWeight: "600", padding: "0.35em 0.8em" }}>
                    {getUserTypeLabel(userData.type)}
                  </Badge>
                  {userData.status && (
                    <Badge bg={userData.status === "APPROVED" ? "success" : userData.status === "PENDING" ? "warning" : "danger"} style={{ fontSize: "0.78rem", fontWeight: "600", padding: "0.35em 0.8em" }}>
                      {getStatusLabel(userData.status)}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="profile-details-list bg-light p-3 rounded-4 mb-4">
                <div className="profile-detail-item">
                  <span className="profile-detail-label">
                    <FiUserCheck size={15} /> 아이디
                  </span>
                  <span className="profile-detail-value">{userData.username}</span>
                </div>
                <div className="profile-detail-item">
                  <span className="profile-detail-label">
                    <FiPhone size={15} /> 연락처
                  </span>
                  <span className="profile-detail-value">{userData.phone || "연락처 미등록"}</span>
                </div>
                <div className="profile-detail-item">
                  <span className="profile-detail-label">
                    <FiCreditCard size={15} /> 환불 계좌
                  </span>
                  <span className="profile-detail-value text-truncate" style={{ maxWidth: "160px" }}>
                    {userData.account || "계좌 미등록"}
                  </span>
                </div>
                {userData.created_at && (
                  <div className="profile-detail-item">
                    <span className="profile-detail-label">
                      <FiCalendar size={15} /> 가입 일자
                    </span>
                    <span className="profile-detail-value">{formatDate(userData.created_at)}</span>
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                onClick={handleOpenEditModal}
                className="btn-custom-outline w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                style={{ borderRadius: "12px", border: "1.5px solid var(--main-color)", fontSize: "0.92rem" }}
              >
                <FiEdit size={16} /> 정보 수정
              </Button>
            </Card.Body>
          </Card>

          {/* 마이페이지 메뉴 */}
          <Card className="profile-card border-0">
            <ListGroup className="menu-list-group" variant="flush">
              <ListGroup.Item
                action
                as={Link}
                to="/my-orders"
              >
                <FiShoppingBag
                  className="me-3"
                  size={18}
                  style={{ color: "var(--main-color)" }}
                />
                <span>주문 내역</span>
              </ListGroup.Item>

              <ListGroup.Item
                action
                onClick={handleLogout}
                className="text-danger"
              >
                <FiLogOut className="me-3" size={18} />
                <span>로그아웃</span>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>

        <Col lg={8}>
          {/* 최근 주문 내역 */}
          <Card className="section-card border-0 mb-4">
            <div className="section-card-header bg-white border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="section-card-title">최근 주문 내역</h2>
                <Button
                  as={Link}
                  to="/my-orders"
                  variant="link"
                  className="p-0 text-decoration-none"
                  style={{ color: "var(--main-color)", fontWeight: "600", fontSize: "0.9rem" }}
                >
                  전체보기
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="teal" style={{ color: "var(--main-color)" }} />
                <p className="text-muted mt-2 mb-0">주문 내역을 가져오고 있습니다...</p>
              </div>
            ) : recentOrders.length === 0 ? (
              <Card.Body className="text-center py-5">
                <p className="text-muted mb-4">최근 주문 내역이 없습니다.</p>
                <Button
                  as={Link}
                  to="/pray"
                  className="btn-custom-primary"
                  style={{ borderRadius: "12px" }}
                >
                  기도 상품 보러가기
                </Button>
              </Card.Body>
            ) : (
              <ListGroup variant="flush">
                {recentOrders.map((order, index) => (
                  <ListGroup.Item
                    key={index}
                    as={Link}
                    to={`/my-orders/${order.id || order.receipt_id || index}`}
                    action
                    className="py-3 px-4 d-flex justify-content-between align-items-center"
                    style={{ borderBottom: "1px solid #f7fafc" }}
                  >
                    <div className="d-flex align-items-center">
                      {order.store?.images?.[0]?.url ? (
                        <div
                          className="me-3"
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            backgroundImage: `url(${order.store.images[0].url})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            flexShrink: 0,
                            border: '1px solid #edf2f7'
                          }}
                        />
                      ) : (
                        <div
                          className="me-3 bg-light d-flex align-items-center justify-content-center"
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "10px",
                            flexShrink: 0,
                            border: '1px solid #edf2f7'
                          }}
                        >
                          <FiShoppingBag size={20} style={{ color: 'var(--main-color)', opacity: 0.5 }} />
                        </div>
                      )}

                      <div>
                        <div className="d-flex align-items-center mb-1">
                          <Badge
                            bg={getStatusColor(order.status)}
                            pill
                            className="me-2"
                            style={{ fontSize: '0.72rem', padding: "0.3em 0.6em", fontWeight: "600" }}
                          >
                            {getStatusText(order.status)}
                          </Badge>
                          <small className="text-muted">
                            {formatDate(order.date)}
                          </small>
                        </div>

                        <div className="fw-bold" style={{ color: "#2d3748", fontSize: "0.98rem" }}>
                          {order.store?.name ||
                            (order.items && order.items[0]?.item?.name) ||
                            (order.items && order.items[0]?.name) ||
                            "주문 상품"}
                          {order.items && order.items.length > 1 &&
                            <span className="text-muted font-normal" style={{ fontWeight: "400", fontSize: "0.85rem" }}> 외 {order.items.length - 1}건</span>
                          }
                        </div>
                      </div>
                    </div>

                    <div className="text-end">
                      <div className="fw-bold" style={{ color: 'var(--main-color)', fontSize: "1.05rem" }}>
                        {formatCurrency(order.price || order.total)}원
                      </div>
                      <small className="text-muted d-block" style={{ fontSize: "0.82rem" }}>
                        {order.status === "DONE" ? "기도 완료" :
                          order.status === "PREPARE" ? "기도 준비중" :
                            order.status === "PROCESS" ? "기도 진행중" : "상세보기"}
                      </small>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card>
        </Col>
      </Row>

      {/* 정보 수정 모달 */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered contentClassName="edit-modal-content">
        <Modal.Header closeButton closeVariant="white" className="edit-modal-header">
          <Modal.Title className="edit-modal-title">회원 정보 수정</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small mb-1">이름</Form.Label>
              <Form.Control
                type="text"
                className="form-input-custom"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
                disabled={editLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small mb-1">연락처</Form.Label>
              <Form.Control
                type="text"
                className="form-input-custom"
                placeholder="예: 010-1234-5678"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                required
                disabled={editLoading}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fw-bold text-muted small mb-1">환불 / 정산 계좌 정보</Form.Label>
              <Form.Control
                type="text"
                className="form-input-custom"
                placeholder="예: 국민은행 123-456789-01-011 (예금주: 만수)"
                value={editFormData.account}
                onChange={(e) => setEditFormData({ ...editFormData, account: e.target.value })}
                disabled={editLoading}
              />
              <Form.Text className="text-muted small">
                * 환불 및 대금 정산에 사용될 정확한 은행명, 계좌번호, 예금주를 입력해주세요.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 px-4 pb-4 gap-2">
            <Button variant="light" onClick={handleCloseEditModal} disabled={editLoading} style={{ borderRadius: "10px", padding: "0.6rem 1.2rem", fontWeight: "600" }}>
              취소
            </Button>
            <Button type="submit" className="btn-custom-primary" disabled={editLoading} style={{ minWidth: "100px" }}>
              {editLoading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  저장 중...
                </>
              ) : (
                "저장"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default MyPage;
